import { isEmpty } from '@jjj/components';
import moment from 'moment';
import { createReducer } from '../../../../../../helpers/reducer';
import { skillsKeysAUS, skillsKeysUSA } from '../../../../../../helpers/skillMapper';
import { isAUS } from '../../../../../../helpers/countriesHelpers';
import actions from './actions';
import { t } from '../../../../../../i18n';
import { MARKUP_TYPES } from './addBulkMarkupModal/addBulkMarkupModal';
import { formatPositinosToSkillRates } from './helpers';

const getInitialField = (fieldName, value = null) => ({
  value,
  error: null,
  name: fieldName,
  touched: false,
});

const initState = {
  isModalVisible: false,
  editForm: {
    future_jobs_date: getInitialField('future_jobs_date', moment().add(1, 'days')),
    positions: undefined,
  },
  formData: {
    apiActions: {},
  },
  isFormDataLoading: false,
  errors: [],
  isBulkMarkupUsed: false,
};

function validate(attr, value) {
  let error = '';

  if (attr === 'future_jobs_date') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.priceList.add_new_pricelist.start_date_label'),
      });
    }
  }

  return error;
}

function onChangeProperty(state, { payload }) {
  const { attr, value, positionIndex, skillIndex } = payload;
  const { editForm } = state;

  if (['bill', 'pay'].includes(attr)) {
    editForm.positions[positionIndex].skills[skillIndex][attr].field = {
      value,
      error: validate(attr, value),
      name: attr,
      touched: true,
    };
  } else {
    editForm[attr] = {
      value,
      error: validate(attr, value),
      name: attr,
      touched: true,
    };
  }

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onClearStore() {
  return {
    ...initState,
    editForm: {
      future_jobs_date: getInitialField('future_jobs_date', moment().add(1, 'days')),
    },
  };
}

function onLoadFormDataRequest(state) {
  return {
    ...state,
    isFormDataLoading: true,
  };
}

function onLoadFormDataSuccess(state, { payload }) {
  const { positions, future_group } = payload;

  const positionsFormFields = positions?.map(({ id, name, skills }) => {
    const skillData = [];

    (isAUS() ? skillsKeysAUS : skillsKeysUSA).forEach(tierKey => {
      const tier = skills.find(item => item.level.id === tierKey);

      const translationKey = tierKey.replace(' ', '_').toLowerCase();

      skillData.push({
        id: tier?.id,
        pay: {
          name: t(
            `dash.units.unit.priceList.columns.${translationKey}_pay_rate_title`,
            {},
            `${tier?.level.name || tierKey} ${t('dash.common.other.pay')} ${t('dash.common.other.rate')}`,
          ),
          type: 'pay',
          field: getInitialField(`pay_${tier?.level?.id}`, tier?.pay),
          disabled: !tier,
        },
        bill: {
          name: t(
            `dash.units.unit.priceList.columns.${translationKey}_bill_rate_title`,
            {},
            `${tier?.level.name || tierKey} ${t('dash.common.other.bill')} ${t('dash.common.other.rate')}`,
          ),
          type: 'bill',
          field: getInitialField(`bill_${tier?.level?.id}`, tier?.bill),
          disabled: !tier,
        },
      });
    });

    return {
      id,
      name,
      skills: skillData,
    };
  });

  return {
    ...state,
    isFormDataLoading: false,
    formData: {
      positions,
      apiActions: payload.actions?.reduce((apiActions, action) => {
        if (action.key) {
          apiActions[action.key] = action;
        }

        return apiActions;
      }, {}),
    },
    editForm: {
      ...state.editForm,
      positions: positionsFormFields,
      future_jobs_date: getInitialField(
        'future_jobs_date',
        future_group?.starts_on ? moment(future_group?.starts_on).format('YYYY-MM-DD') : moment().add(1, 'days'),
      ),
    },
  };
}

function onApplyBulkMarkup(state, { payload }) {
  const { editForm } = state;
  const { markup, value } = payload;

  editForm.positions.forEach(position => {
    position.skills.forEach(({ bill, pay }) => {
      if (!bill.disabled) {
        if (markup === MARKUP_TYPES.PERCENT) {
          bill.field.value = parseFloat(pay.field.value) + (parseFloat(pay.field.value) * parseFloat(value)) / 100;
        } else if (markup === MARKUP_TYPES.CURRENCY) {
          bill.field.value = parseFloat(pay.field.value) + parseFloat(value);
        }
      }
    });
  });

  return {
    ...state,
    loading: true,
    editForm,
    isBulkMarkupUsed: true,
  };
}

function onLoadFormDataError(state, { payload }) {
  const { errors } = payload;

  return {
    ...state,
    isFormDataLoading: false,
    errors,
  };
}

function onCreatePriceListError(state, { payload }) {
  const { errors } = payload;
  const { editForm } = state;
  const { positions } = editForm;

  const skillRates = formatPositinosToSkillRates(positions);
  const skills = positions
    .flatMap(position => position.skills)
    .reduce((acc, skill) => ({ ...acc, [skill.id]: skill }), {});

  Object.entries(errors.skill_rates).forEach(([index, errorFields]) => {
    const skillId = skillRates[index].skill_id;
    const skill = skills[skillId];

    Object.entries(errorFields).forEach(([field, messages]) => {
      if (skill[field]) {
        skill[field].field.error = messages.join(', ');
        skill[field].field.touched = true;
      }
    });
  });

  if (errors.starts_on) {
    editForm.future_jobs_date.error = errors.starts_on.join(',');
    editForm.future_jobs_date.touched = true;
  }

  return {
    ...state,
    isFormDataLoading: false,
    editForm,
  };
}

export default createReducer(initState, {
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.LOAD_FORM_DATA_REQUEST]: onLoadFormDataRequest,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.LOAD_FORM_DATA_ERROR]: onLoadFormDataError,
  [actions.APPLY_BULK_MARKUP]: onApplyBulkMarkup,
  [actions.CREATE_PRICE_LIST_ERROR]: onCreatePriceListError,
  [actions.CLEAR_STORE]: onClearStore,
});
